import { faFile } from '@fortawesome/free-solid-svg-icons';
import { type JSX, useMemo, useState } from 'react';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { Button } from 'Components/Buttons/Buttons';
import { CircleIndicatorControlEffectiveness } from 'Components/CircleIndicators/CircleIndicatorControlEffectiveness';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldTextAreaAi } from 'Components/FormField/FormFieldTextAreaAi/FormFieldTextAreaAi';
import { Text } from 'Components/Text/Text';
import { SummitGuideOverwriteNotice } from 'Components/UcrmTprm/SummitGuideOverwriteNotice/SummitGuideOverwriteNotice';
import { useAiContentAdvisor } from 'Hooks/AiContentAdvisor';
import { AiContentAdvisorFeature } from 'Models/ArtificialIntelligence';
import { DueDiligenceControl } from 'Models/DueDiligence';
import { UploadedFile } from 'Models/Files';
import { Effectiveness, EffectivenessSelectOptions } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { TprmOrUcrmDueDiligenceAssessment } from 'Models/UcrmTprm';

import styles from './ControlAssessment.module.css';
import { ManageControlFilesModal } from '../ManageControlFilesModal/ManageControlFilesModal';

export interface ControlAssessmentProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    control: DueDiligenceControl;
    documentApi: DocumentApi;
    existingPendingFiles: File[];
    lastSavedAssessment: TprmOrUcrmDueDiligenceAssessment;
    module: DueDiligenceModule;
    onAiError: (message: string) => void;
    onAssessmentUpdated: (updatedAssessment: TprmOrUcrmDueDiligenceAssessment) => void;
    onChange: (effectiveness: Effectiveness | undefined, comments: string, mappedFiles?: UploadedFile[]) => void;
    onSaveFileChanges: (newPendingFiles: File[], selectedPendingFiles: File[], selectedUploadedFiles: UploadedFile[]) => void;
    selectedPendingFiles: File[];

    disabled?: boolean;
}

/**
 * Renders the UI for assessing a control within a due diligence assessment.
 * Includes file management, comments, effectiveness, and SummIT Guide™ assessment.
 */
export const ControlAssessment = (props: ControlAssessmentProps): JSX.Element => {
    const [showAddFilesModal, setShowAddFilesModal] = useState(false);

    const commentsAi = useAiContentAdvisor(props.artificialIntelligenceApi, props.control.control_assessment_human.comments ?? '', props.module === ProductModule.UCRM ? AiContentAdvisorFeature.UCRM_ASSESSMENT_CONTROL_COMMENTS : AiContentAdvisorFeature.TPRM_ASSESSMENT_CONTROL_COMMENTS, props.onAiError);

    const lastSavedEffectiveness = useMemo(() => {
        return props.lastSavedAssessment.control_frameworks
            .flatMap((framework) => framework.control_groups)
            .flatMap((group) => group.controls)
            .find((control) => control.identifier === props.control.identifier)?.control_assessment_human.effectiveness;
    }, [props.lastSavedAssessment, props.control.identifier]);

    const uploadedFiles = props.control.control_assessment_human.files;
    const pendingFiles = props.selectedPendingFiles;
    const hasFiles = uploadedFiles.length > 0 || pendingFiles.length > 0;

    return (
        <div className={styles.container}>
            <Text variant="Header3">Control Assessment</Text>
            <div className={`${styles.fieldContainer} ${styles.effectivenessAndNotice}`}>
                <FormFieldSelect formFieldId={`control-effectiveness-${props.control.identifier}`} formFieldLabel="Effectiveness" disabled={props.disabled} options={EffectivenessSelectOptions} selectedOption={props.control.control_assessment_human.effectiveness} handleChange={(value: ChangeEventType) => props.onChange(value as Effectiveness, props.control.control_assessment_human.comments ?? '')} />
                <SummitGuideOverwriteNotice comparisons={[{ recommended: props.control.control_assessment_ai?.recommended_effectiveness, saved: lastSavedEffectiveness }]} />
            </div>
            {props.control.control_assessment_ai && (
                <div className={styles.summitGuideSection}>
                    <Text variant="Header4">SummIT Guide™ Analysis</Text>
                    <div>
                        <div className={styles.circleRow}>
                            <Text noStyles>Recommended overall control effectiveness:</Text>
                            <CircleIndicatorControlEffectiveness effectiveness={props.control.control_assessment_ai.recommended_effectiveness} />
                        </div>
                        <div className={styles.aiReviewComments}>
                            <Text preserveLineBreaks noStyles>
                                {props.control.control_assessment_ai.comments}
                            </Text>
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.fieldContainer}>
                <FormFieldTextAreaAi formFieldId={`control-comments-${props.control.identifier}`} formFieldLabel="Comments" disabled={props.disabled} value={props.control.control_assessment_human.comments ?? ''} handleChange={(event: React.FormEvent<HTMLInputElement>) => props.onChange(props.control.control_assessment_human.effectiveness, event.currentTarget.value)} aiContentAdvisorValues={commentsAi} />
            </div>
            <div className={styles.fieldContainer}>
                <div className={styles.filesHeader}>
                    <Text variant="Header4">Files</Text>
                    <Button variant="secondary" onClick={() => setShowAddFilesModal(true)} fontAwesomeImage={faFile} disabled={props.disabled}>
                        Manage Files
                    </Button>
                </div>
                {hasFiles ? (
                    <div className={styles.filesList}>
                        {[...uploadedFiles.map((file) => ({ type: 'uploaded' as const, file, name: file.filename })), ...pendingFiles.map((file) => ({ type: 'pending' as const, file, name: file.name }))]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((item) =>
                                item.type === 'uploaded' ? (
                                    <UploadedFileAndState key={item.file.file_id} documentApi={props.documentApi} file={item.file} onDownloadError={(msg) => console.error(msg)} />
                                ) : (
                                    <Text key={item.file.name} variant="Text3" color="darkGray" noStyles>
                                        {item.file.name} (will upload on save)
                                    </Text>
                                )
                            )}
                    </div>
                ) : (
                    <Text variant="Text3" color="darkGray" noStyles>
                        No files mapped to this control.
                    </Text>
                )}
            </div>
            {showAddFilesModal && <ManageControlFilesModal existingPendingFiles={props.existingPendingFiles} hideModal={() => setShowAddFilesModal(false)} initialControl={props.control} initialSelectedPendingFiles={props.selectedPendingFiles} lastSavedAssessment={props.lastSavedAssessment} module={props.module} onSave={props.onSaveFileChanges} />}
        </div>
    );
};
